import type {
    CatalogImportCode,
    CatalogImportLineRequest,
    CatalogImportNameDecision,
    CatalogImportNamesRequest,
} from '@/api/handlers/resources/catalogImports';
import type {
    CatalogImportLineResult,
    CatalogImportNamesResult,
    CatalogImportNameSuggestion,
    CatalogImportRecord,
    CatalogImportResult,
} from '@/api/models';
import { CELL_NOT_A_NUMBER } from '@/pages/catalogImport/notices';
import {
    allFixesPlan,
    bulkFixGroupOf,
    bulkFixGroups,
    groupPickPatches,
    makeMainPatches,
    mergedPatches,
    withPatches,
    type BulkFixCode,
    type GroupPick,
} from './catalogImportBulkFixes';

/** A line's number fields: amounts in GEL, and counts. */
export type ParsedNumberField =
    'quantity' | 'cost' | 'salePrice' | 'wholesalePrice' | 'minimumQuantity';

/**
 * Number cells the parse could not read, as the file has them, by field.
 * The API takes numbers only, so the line leaves such a field off; the
 * store marks the row as needing attention (`withUnreadableCells`) until
 * the row drawer's Save writes a number, or a blank, in its place.
 */
export type UnreadableCells = Partial<Record<ParsedNumberField, string>>;

/**
 * One parsed row of the file, as the request will send it. Whether it is
 * excluded is not on the line: `exclusions` holds that, so excluding and
 * including never rewrite a line.
 */
export type ParsedLine = Omit<CatalogImportLineRequest, 'excluded'> & {
    /** Client only: the request builder leaves it off (`UnreadableCells`). */
    unreadable?: UnreadableCells;
};

/** The decisions on the file's unknown brands, categories and suppliers. */
export type NamesDecisions = CatalogImportNamesRequest;

/**
 * The typo suggestion each text of the file was last given, by kind and by
 * the text trimmed and lower-cased (as the server matches texts). The
 * server suggests only for a text sent as Create, so the answer to a kept
 * preselection carries none; this is what the step proposed for it.
 */
export type NameSuggestions = Record<
    keyof CatalogImportNamesResult,
    ReadonlyMap<string, CatalogImportNameSuggestion>
>;

/** A text as the server matches it, and as `NameSuggestions` keys it. */
export const nameTextKey = (text: string): string => text.trim().toLowerCase();

/** A patch to one parsed line; the row number is the line's identity. */
export type ParsedLinePatch = Partial<Omit<ParsedLine, 'rowNumber'>>;

export type CatalogImportBranch = { id: string; name: string };

export type CatalogImportFile = {
    name: string;
    /** SHA-256 of the file, hex: how the server recognises a re-upload. */
    hash: string;
    rowCount: number;
    /** Headers the parse could not place, as written; their columns were not read. */
    ignoredColumns: string[];
};

export type PreviewStatus = 'idle' | 'running' | 'done' | 'failed';

/** The import itself: sent and not answered yet (`running`), or not (`idle`). */
export type ImportStatus = 'idle' | 'running';

/**
 * The bulk action a snapshot was taken before: one kind's fix, every fix
 * at once ("Import with suggested fixes"), Exclude all or Include all back.
 */
export type CatalogImportBulkAction =
    BulkFixCode | 'allFixes' | 'excludeAll' | 'includeAll';

/**
 * The owner's work on the rows as it was before a bulk action: what `undo`
 * restores. `rows` is how many rows the action changed. The name choices
 * are not in it: a bulk action never changes them, so a choice made after
 * it survives its undo (bos-app#213).
 */
export type CatalogImportSnapshot = {
    action: CatalogImportBulkAction;
    rows: number;
    lines: ParsedLine[];
    exclusions: ReadonlySet<number>;
    editedRows: ReadonlySet<number>;
};

export type CatalogImportState = {
    branch: CatalogImportBranch | null;
    file: CatalogImportFile | null;
    lines: ParsedLine[];
    /**
     * The owner's decisions on the file's names, checked or not: Check
     * products writes them, and so does Back from Names, so they can run
     * ahead of the preview (`checkedDecisions`).
     */
    decisions: NamesDecisions;
    /**
     * The decisions the current preview answered: what its check (or the
     * refused import that brought it) sent. The Names step reads what each
     * name was answered as from these, never from `decisions`.
     */
    checkedDecisions: NamesDecisions;
    /** What the check or import on its way sent; its answer answered these. */
    sentDecisions: NamesDecisions;
    /**
     * Every typo suggestion an answer made, kept while later answers carry
     * none for the text: what a kept preselection is compared with
     * (`changedDecisionCount` in `names/`). Names belong to the
     * organisation, so a new branch keeps them.
     */
    suggestions: NameSuggestions;
    /**
     * The names the latest answer listed, kept while a new branch has dropped
     * the preview: what the name choices are counted against until Upload's
     * check answers again (`importWorkOf` in `work/`). Names belong to the
     * organisation, so a new branch keeps them; a new file drops them.
     */
    lastNames: CatalogImportNamesResult | null;
    preview: CatalogImportResult | null;
    previewStatus: PreviewStatus;
    /** When the current preview was answered, ISO date-time; `null` before. */
    lastCheckedAt: string | null;
    /** Row numbers the user left out. */
    exclusions: ReadonlySet<number>;
    /**
     * Row numbers the row drawer's Save changed (`updateLine`, `updateGroup`),
     * whether or not they are excluded now: part of the work a new file
     * would lose (`importWorkOf` in `work/`).
     */
    editedRows: ReadonlySet<number>;
    /**
     * Upload is reading a file: from the pick or drop until the parse ends
     * (the file reaches the store, or the read stops, is cancelled or is
     * replaced). The strip offers no links meanwhile (`linkableSteps`).
     */
    isReadingFile: boolean;
    /**
     * The snapshots taken before each bulk action not undone yet, the most
     * recent last: `undo` restores the last one. The completed import,
     * another file, a new file and leaving the wizard clear them; a re-check
     * does not.
     */
    undoStack: readonly CatalogImportSnapshot[];
    /** An import is on its way: the step holds still until it answers. */
    importStatus: ImportStatus;
    /** The completed import; set, the flow is past the point of losing work. */
    record: CatalogImportRecord | null;
    /**
     * The server refused the branch to this user (`BRANCH_NOT_ALLOWED` on a
     * preview, the import or the branch's history): nothing can be checked or
     * imported for it, so the in-shell page says so instead of the step, and
     * leaving it loses nothing (`isDirty`).
     */
    scopeRefused: boolean;
};

export type CatalogImportAction =
    | { type: 'setBranch'; branch: CatalogImportBranch | null }
    | { type: 'setFile'; file: CatalogImportFile | null }
    | { type: 'setLines'; lines: ParsedLine[] }
    | { type: 'setDecisions'; decisions: NamesDecisions }
    | { type: 'fileReadStarted' }
    | { type: 'fileReadEnded' }
    | { type: 'previewStarted' }
    | { type: 'previewFailed'; error?: unknown }
    | { type: 'setPreview'; preview: CatalogImportResult; checkedAt: string }
    | { type: 'excludeRows'; rowNumbers: readonly number[] }
    | { type: 'includeRows'; rowNumbers: readonly number[] }
    | { type: 'updateLine'; rowNumber: number; patch: ParsedLinePatch }
    | {
          type: 'updateGroup';
          rowNumbers: readonly number[];
          patch: ParsedLinePatch;
      }
    /** One kind's default: every group of it, or the group of `rowNumber` (the drawer). */
    | { type: 'applyFix'; code: BulkFixCode; rowNumber?: number }
    /** The drawer's pick for the group of `rowNumber`, as a bulk action. */
    | { type: 'applyGroupPick'; rowNumber: number; pick: GroupPick }
    /**
     * Every default, kind after kind, as one bulk action: over the problems
     * of the preview, or of `against` (an answer to the lines as they are
     * now). With `join`, the rows it changes join the latest "every fix"
     * snapshot instead of pushing another, so the two are one undo step.
     */
    | {
          type: 'applyAllFixes';
          against?: CatalogImportResult;
          join?: boolean;
      }
    | { type: 'excludeAll'; rowNumbers: readonly number[] }
    | { type: 'includeAll'; rowNumbers: readonly number[] }
    | { type: 'makeMainBarcode'; rowNumber: number }
    | { type: 'undo' }
    | { type: 'importStarted' }
    | { type: 'importFailed'; error?: unknown }
    | { type: 'setRecord'; record: CatalogImportRecord | null }
    | { type: 'branchRefused' }
    | { type: 'resetImport' }
    | { type: 'reset' };

const emptyDecisions = (): NamesDecisions => ({
    brands: [],
    categories: [],
    suppliers: [],
});

const emptySuggestions = (): NameSuggestions => ({
    brands: new Map(),
    categories: new Map(),
    suppliers: new Map(),
});

/**
 * The remembered suggestions with an answer's: a text the answer suggests
 * for takes its suggestion, and a text it suggests nothing for keeps the
 * one remembered.
 */
const withSuggestions = (
    remembered: NameSuggestions,
    names: CatalogImportNamesResult,
): NameSuggestions => {
    const merge = (
        kind: keyof CatalogImportNamesResult,
    ): ReadonlyMap<string, CatalogImportNameSuggestion> => {
        const merged = new Map(remembered[kind]);
        for (const entry of names[kind]) {
            if (entry.suggestion) {
                merged.set(nameTextKey(entry.text), entry.suggestion);
            }
        }
        return merged;
    };
    return {
        brands: merge('brands'),
        categories: merge('categories'),
        suppliers: merge('suppliers'),
    };
};

/**
 * Whether the server takes the decision: Use existing needs its target,
 * Rename its name and Nest under its parent; Create and a text alone (the
 * server's own match) need nothing. A request carrying one without its
 * value is refused whole.
 */
const isSendable = (decision: CatalogImportNameDecision): boolean => {
    switch (decision.action) {
        case 'useExisting':
            return decision.target != null;
        case 'rename':
            return (decision.name ?? '').trim() !== '';
        case 'nest':
            return (decision.parentId ?? '') !== '';
        default:
            return true;
    }
};

/**
 * The decisions a check or the import sends: the store's, without those
 * still missing their value. Back from Names keeps such a choice so the
 * owner finds it again, and Check products waits until it is completed;
 * a check sent before then (Upload's, after another branch) answers that
 * name as undecided.
 */
export const sendableDecisions = (
    decisions: NamesDecisions,
): NamesDecisions => ({
    brands: decisions.brands.filter(isSendable),
    categories: decisions.categories.filter(isSendable),
    suppliers: decisions.suppliers.filter(isSendable),
});

export const createInitialCatalogImportState = (): CatalogImportState => ({
    branch: null,
    file: null,
    lines: [],
    decisions: emptyDecisions(),
    checkedDecisions: emptyDecisions(),
    sentDecisions: emptyDecisions(),
    suggestions: emptySuggestions(),
    lastNames: null,
    preview: null,
    previewStatus: 'idle',
    lastCheckedAt: null,
    exclusions: new Set(),
    editedRows: new Set(),
    undoStack: [],
    isReadingFile: false,
    importStatus: 'idle',
    record: null,
    scopeRefused: false,
});

const BRANCH_SCOPE_REFUSAL: CatalogImportCode = 'BRANCH_NOT_ALLOWED';

/**
 * Whether a failed preview, import or history read was the server refusing
 * the branch to this user: a BranchAdmin naming another branch
 * (`BRANCH_NOT_ALLOWED`, 403).
 */
export const isBranchScopeRefusal = (error: unknown): boolean =>
    typeof error === 'object' &&
    error !== null &&
    (error as { code?: unknown }).code === BRANCH_SCOPE_REFUSAL;

/** What a new file or a new branch invalidates: everything derived from it. */
const withoutPreview = (state: CatalogImportState): CatalogImportState => ({
    ...state,
    checkedDecisions: emptyDecisions(),
    preview: null,
    previewStatus: 'idle',
    lastCheckedAt: null,
});

/**
 * A check's answer with the cells the parse could not read as numbers: the
 * server never saw them (the line leaves such a field off), so it cannot
 * say so. Each becomes a problem of its row (`CELL_NOT_A_NUMBER`, first,
 * naming the field and the text), and the row needs attention: Import
 * waits until the row is corrected or excluded. A row the answer excluded
 * is left as it is, and so is the answer when no line has such a cell.
 */
export const withUnreadableCells = (
    preview: CatalogImportResult,
    lines: readonly ParsedLine[],
): CatalogImportResult => {
    const unreadable = new Map<number, UnreadableCells>();
    for (const line of lines) {
        if (line.unreadable && Object.keys(line.unreadable).length > 0) {
            unreadable.set(line.rowNumber, line.unreadable);
        }
    }
    if (unreadable.size === 0) {
        return preview;
    }
    const withProblems = (
        result: CatalogImportLineResult,
    ): CatalogImportLineResult => {
        const cells = unreadable.get(result.rowNumber);
        if (!cells || result.status === 'excluded') {
            return result;
        }
        return {
            ...result,
            status: 'needsAttention',
            problems: [
                ...Object.entries(cells).map(([field, value]) => ({
                    code: CELL_NOT_A_NUMBER,
                    fields: [field],
                    params: { value },
                })),
                ...result.problems,
            ],
        };
    };
    return { ...preview, lines: preview.lines.map(withProblems) };
};

const withEdited = (
    editedRows: ReadonlySet<number>,
    rowNumbers: readonly number[],
): ReadonlySet<number> => new Set([...editedRows, ...rowNumbers]);

const patchLines = (
    lines: ParsedLine[],
    rowNumbers: ReadonlySet<number>,
    patch: ParsedLinePatch,
): ParsedLine[] =>
    lines.map((line) =>
        rowNumbers.has(line.rowNumber) ? { ...line, ...patch } : line,
    );

/**
 * The state with a snapshot of the owner's work pushed for a bulk action
 * that changes `rows` rows; one that changes none pushes nothing.
 */
const withSnapshot = (
    state: CatalogImportState,
    action: CatalogImportBulkAction,
    rows: number,
): CatalogImportState =>
    rows === 0
        ? state
        : {
              ...state,
              undoStack: [
                  ...state.undoStack,
                  {
                      action,
                      rows,
                      lines: state.lines,
                      exclusions: state.exclusions,
                      editedRows: state.editedRows,
                  },
              ],
          };

/** The lines patched, every patched row remembered as edited. */
const withLinePatches = (
    state: CatalogImportState,
    patches: ReadonlyMap<number, ParsedLinePatch>,
): CatalogImportState =>
    patches.size === 0
        ? state
        : {
              ...state,
              lines: withPatches(state.lines, patches),
              editedRows: withEdited(state.editedRows, [...patches.keys()]),
          };

/** One kind's default, to every group of it (a bulk action) or one group. */
const applyFix = (
    state: CatalogImportState,
    code: BulkFixCode,
    rowNumber: number | undefined,
): CatalogImportState => {
    if (rowNumber !== undefined) {
        const group = bulkFixGroupOf(state, code, rowNumber);
        return group ? withLinePatches(state, group.patches) : state;
    }
    const patches = mergedPatches(bulkFixGroups(state, code));
    return withLinePatches(withSnapshot(state, code, patches.size), patches);
};

/** The drawer's pick for one group: a bulk action of its kind, so it can be undone. */
const applyGroupPick = (
    state: CatalogImportState,
    rowNumber: number,
    pick: GroupPick,
): CatalogImportState => {
    const patches = groupPickPatches(state, rowNumber, pick);
    return withLinePatches(
        withSnapshot(state, pick.code, patches.size),
        patches,
    );
};

/**
 * The latest snapshot, an "every fix" one, counting the rows that differ
 * from it once `lines` are written: the step it stands for grows by them.
 */
const withJoinedSnapshot = (
    state: CatalogImportState,
    lines: readonly ParsedLine[],
): CatalogImportState | null => {
    const latest = state.undoStack.at(-1);
    if (latest?.action !== 'allFixes') {
        return null;
    }
    const before = new Map(latest.lines.map((line) => [line.rowNumber, line]));
    const rows = lines.filter(
        (line) => before.get(line.rowNumber) !== line,
    ).length;
    return {
        ...state,
        undoStack: [...state.undoStack.slice(0, -1), { ...latest, rows }],
    };
};

const applyAllFixes = (
    state: CatalogImportState,
    against: CatalogImportResult | undefined,
    join: boolean,
): CatalogImportState => {
    const plan = allFixesPlan(
        against === undefined ? state : { ...state, preview: against },
    );
    if (plan.changedRows.length === 0) {
        return state;
    }
    const snapshotted =
        (join ? withJoinedSnapshot(state, plan.lines) : null) ??
        withSnapshot(state, 'allFixes', plan.changedRows.length);
    return {
        ...snapshotted,
        lines: plan.lines,
        editedRows: withEdited(state.editedRows, plan.changedRows),
    };
};

/** Exclude all or Include all back: the rows whose exclusion it changes. */
export const exclusionChanges = (
    state: Pick<CatalogImportState, 'exclusions'>,
    rowNumbers: readonly number[],
    exclude: boolean,
): number[] => [
    ...new Set(
        rowNumbers.filter((row) => state.exclusions.has(row) !== exclude),
    ),
];

const withExclusions = (
    state: CatalogImportState,
    rowNumbers: readonly number[],
    exclude: boolean,
): CatalogImportState => {
    const changed = exclusionChanges(state, rowNumbers, exclude);
    if (changed.length === 0) {
        return state;
    }
    const exclusions = new Set(state.exclusions);
    for (const row of changed) {
        if (exclude) {
            exclusions.add(row);
        } else {
            exclusions.delete(row);
        }
    }
    return {
        ...withSnapshot(
            state,
            exclude ? 'excludeAll' : 'includeAll',
            changed.length,
        ),
        exclusions,
    };
};

/** The latest snapshot restored and taken off the stack; nothing without one. */
const undo = (state: CatalogImportState): CatalogImportState => {
    const snapshot = state.undoStack.at(-1);
    if (!snapshot) {
        return state;
    }
    return {
        ...state,
        lines: snapshot.lines,
        exclusions: snapshot.exclusions,
        editedRows: snapshot.editedRows,
        undoStack: state.undoStack.slice(0, -1),
    };
};

/**
 * The import flow's state, shared by every step of the wizard and the
 * in-shell page. Pure: time comes in on the action (`checkedAt`).
 *
 * - A new **file** starts over from it: lines, decisions, preview,
 *   exclusions, edited rows and record are dropped. The branch stays.
 * - **Reading a file** (`fileReadStarted` … `fileReadEnded`) is Upload's
 *   parse before the file reaches the store; `setFile` ends it too, as do
 *   another file and a reset.
 * - A new **branch** drops the preview, which was answered for the old one;
 *   the work on the file (edits, decisions, exclusions) stays.
 * - Line edits, decisions and exclusions leave the preview as it was: the
 *   steps decide when to re-check. Every row a line edit patches is
 *   remembered as edited (`editedRows`).
 * - Every check and the import send the store's decisions as
 *   `sendableDecisions` has them, so starting one remembers those
 *   (`sentDecisions`), and its stored answer is the answer to them
 *   (`checkedDecisions`). Only the latest check's answer is ever stored
 *   (the provider's revision), and a failed one leaves both preview and
 *   `checkedDecisions` as they were.
 * - A **preview** is stored with the cells the parse could not read as
 *   numbers as problems of their rows (`withUnreadableCells`). Its typo
 *   suggestions join `suggestions`; a text it suggests nothing for keeps
 *   the one remembered, and its names are kept as `lastNames`. A new file
 *   and another file clear both; a new branch keeps them (names belong to
 *   the organisation), so the work still counts the name choices while
 *   the preview is dropped.
 * - An **import** runs from `importStarted` until its answer: the record
 *   (`setRecord`) or a refusal (`importFailed`; a refusal that carries a
 *   fresh preview stores it with `setPreview` as well).
 * - **Another file** (`resetImport`, Result's Import another file) drops
 *   everything but the branch, the record and edited rows included: the
 *   next file is for the same branch unless the user picks another.
 * - **Bulk fixes** (`applyFix` for one kind, `applyAllFixes` for every
 *   kind) write the defaults `catalogImportBulkFixes` computes; every row
 *   they change joins `editedRows`. `applyAllFixes` can work from an
 *   answer that is not the preview (`against`: "Import with suggested
 *   fixes" checking the lines it wrote) and join the latest "every fix"
 *   snapshot (`join`), so the defaults that answer calls for are undone
 *   with the rest. `applyFix` with a `rowNumber` is the
 *   drawer's: that row's group only, like a drawer edit. The drawer's own
 *   pick for its group (`applyGroupPick`: a variant figure's value, the
 *   SKU row whose fields every row takes, or the IMEI move or shared
 *   barcode default for that group alone) is a bulk action of its kind, so
 *   its toast can offer Undo (bos-app#219, bos-app#228).
 * - **Make main** sets `primaryBarcode` on one line and clears it on the
 *   variant's others, like a drawer edit.
 * - **Undo.** Every bulk action (one kind's fix, the drawer's group pick,
 *   every fix, Exclude all, Include all back) that changes a row first
 *   pushes a snapshot of the lines, exclusions and edited rows
 *   (`undoStack`). `undo` restores the latest one and takes it off, so a
 *   drawer edit made after the action goes with it, while a name choice
 *   made after it stays; the preview stays until the step re-checks.
 *   A re-check keeps the stack; the completed import, another file, a new
 *   file and a reset (leaving the wizard) clear it.
 * - A preview or import that failed because the server refused the branch
 *   (`isBranchScopeRefusal`) marks `scopeRefused`, and so does a refused
 *   read of the branch's history (`branchRefused`); a new branch, a new file
 *   or another file clears it.
 */
export const catalogImportReducer = (
    state: CatalogImportState,
    action: CatalogImportAction,
): CatalogImportState => {
    switch (action.type) {
        case 'setBranch':
            return withoutPreview({
                ...state,
                branch: action.branch,
                scopeRefused: false,
            });
        case 'setFile':
            return {
                ...createInitialCatalogImportState(),
                branch: state.branch,
                file: action.file,
            };
        case 'setLines':
            return { ...state, lines: action.lines };
        case 'setDecisions':
            return { ...state, decisions: action.decisions };
        case 'fileReadStarted':
            return state.isReadingFile
                ? state
                : { ...state, isReadingFile: true };
        case 'fileReadEnded':
            return state.isReadingFile
                ? { ...state, isReadingFile: false }
                : state;
        case 'previewStarted':
            return {
                ...state,
                previewStatus: 'running',
                sentDecisions: sendableDecisions(state.decisions),
            };
        case 'previewFailed':
            return {
                ...state,
                previewStatus: 'failed',
                scopeRefused:
                    state.scopeRefused || isBranchScopeRefusal(action.error),
            };
        case 'setPreview':
            return {
                ...state,
                checkedDecisions: state.sentDecisions,
                suggestions: withSuggestions(
                    state.suggestions,
                    action.preview.names,
                ),
                lastNames: action.preview.names,
                preview: withUnreadableCells(action.preview, state.lines),
                previewStatus: 'done',
                lastCheckedAt: action.checkedAt,
            };
        case 'excludeRows': {
            const exclusions = new Set(state.exclusions);
            action.rowNumbers.forEach((row) => exclusions.add(row));
            return { ...state, exclusions };
        }
        case 'includeRows': {
            const exclusions = new Set(state.exclusions);
            action.rowNumbers.forEach((row) => exclusions.delete(row));
            return { ...state, exclusions };
        }
        case 'updateLine':
            return {
                ...state,
                lines: patchLines(
                    state.lines,
                    new Set([action.rowNumber]),
                    action.patch,
                ),
                editedRows: withEdited(state.editedRows, [action.rowNumber]),
            };
        case 'updateGroup':
            return {
                ...state,
                lines: patchLines(
                    state.lines,
                    new Set(action.rowNumbers),
                    action.patch,
                ),
                editedRows: withEdited(state.editedRows, action.rowNumbers),
            };
        case 'applyFix':
            return applyFix(state, action.code, action.rowNumber);
        case 'applyGroupPick':
            return applyGroupPick(state, action.rowNumber, action.pick);
        case 'applyAllFixes':
            return applyAllFixes(state, action.against, action.join === true);
        case 'excludeAll':
            return withExclusions(state, action.rowNumbers, true);
        case 'includeAll':
            return withExclusions(state, action.rowNumbers, false);
        case 'makeMainBarcode':
            return withLinePatches(
                state,
                makeMainPatches(state, action.rowNumber),
            );
        case 'undo':
            return undo(state);
        case 'importStarted':
            return {
                ...state,
                importStatus: 'running',
                sentDecisions: sendableDecisions(state.decisions),
            };
        case 'importFailed':
            return {
                ...state,
                importStatus: 'idle',
                scopeRefused:
                    state.scopeRefused || isBranchScopeRefusal(action.error),
            };
        case 'setRecord':
            return {
                ...state,
                record: action.record,
                importStatus: 'idle',
                // Imported: nothing before it can be undone any more.
                undoStack: action.record === null ? state.undoStack : [],
            };
        case 'branchRefused':
            return { ...state, scopeRefused: true };
        case 'resetImport':
            return {
                ...createInitialCatalogImportState(),
                branch: state.branch,
            };
        case 'reset':
            return createInitialCatalogImportState();
    }
};
